import { CanvasTexture, SpriteMaterial } from "three"

// Returns the DOMRect bbox of a path string via a temporary off-screen SVG element.
const getPathBBox = (markerPath: string): DOMRect => {
  const svgNS = "http://www.w3.org/2000/svg"
  const svg = document.createElementNS(svgNS, "svg")
  const path = document.createElementNS(svgNS, "path")
  svg.setAttribute("width", "0")
  svg.setAttribute("height", "0")
  svg.style.position = "absolute"
  path.setAttribute("d", markerPath)
  svg.appendChild(path)
  document.body.appendChild(svg)
  try {
    return (path as SVGPathElement).getBBox()
  } finally {
    document.body.removeChild(svg)
  }
}

// Returns scale + dx/dy to place a path centered within a square viewBox.
// Pass `scale` to center at a known scale, or `targetSize` to fit the path's
// bbox to that size and derive the scale automatically.
export const getPathTransform = (
  markerPath: string,
  viewBoxWidth: number,
  {
    scale,
    targetSize,
    viewBoxHeight,
  }: { scale?: number; targetSize?: number; viewBoxHeight?: number } = {}
): { scale: number; dx: number; dy: number } => {
  const b = getPathBBox(markerPath)
  const vbH = viewBoxHeight ?? viewBoxWidth
  const s =
    targetSize != null ? targetSize / Math.max(b.width, b.height) : scale ?? 1
  return {
    scale: s,
    dx: viewBoxWidth / 2 - s * (b.x + b.width / 2),
    dy: vbH / 2 - s * (b.y + b.height / 2),
  }
}

// Thin wrapper around getPathTransform for centering a path at a known scale.
export const getPathCenteringOffset = (
  markerPath: string,
  viewBoxSize: number,
  scale: number = 1
) => getPathTransform(markerPath, viewBoxSize, { scale })

// Builds an SVG string from a path with fill and optional stroke, scaled to fill width×height.
// The path is fitted to the viewBox using targetSize so strokeWidth is in output-pixel units,
// matching the behavior of createSVGPathFromMarkerSymbol (section-icon path).
export const buildPlainSvg = (params: {
  path?: string
  width?: number
  height?: number
  fill?: string
  stroke?: string
  strokeWidth?: number
}): string | null => {
  if (!params?.path) return null
  const width = params.width ?? 24
  const height = params.height ?? width
  const fill = params.fill ?? "none"
  const stroke = params.stroke
  const strokeWidth = params.strokeWidth ?? 1
  const strokeAttrs = stroke
    ? ` stroke="${stroke}" stroke-width="${strokeWidth}" stroke-linejoin="round"`
    : ""
  const targetSize = Math.min(width, height)
  const {
    scale: s,
    dx,
    dy,
  } = getPathTransform(params.path, width, {
    targetSize,
    viewBoxHeight: height,
  })
  const svgContent = `<g transform="translate(${dx} ${dy}) scale(${s})"><path d="${params.path}" fill="${fill}"${strokeAttrs}/></g>`
  const pad = stroke ? strokeWidth / 2 : 0
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${-pad} ${-pad} ${
    width + 2 * pad
  } ${height + 2 * pad}">${svgContent}</svg>`
}

// Draws img centered in a size×size region, scaled and nudged by offset (pixels), clipped to a rounded square.
export const drawCenteredImage = (
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  {
    size,
    scale,
    offsetX,
    offsetY,
    radiusRatio = 0.125,
  }: {
    size: number
    scale: number
    offsetX: number
    offsetY: number
    radiusRatio?: number
  }
): void => {
  const imgSize = Math.round(size * scale)
  const imgX = Math.round((size - imgSize) / 2 + offsetX)
  const imgY = Math.round((size - imgSize) / 2 + offsetY)
  const radius = imgSize * radiusRatio
  ctx.save()
  if (radius > 0) {
    ctx.beginPath()
    ctx.roundRect(imgX, imgY, imgSize, imgSize, radius)
    ctx.clip()
  }
  ctx.drawImage(img, imgX, imgY, imgSize, imgSize)
  ctx.restore()
}

// Converts an SVG string to an HTMLImageElement via a blob URL.
export const loadSvgAsImage = (svg: string): Promise<HTMLImageElement> => {
  const blob = new Blob([svg], { type: "image/svg+xml" })
  const url = URL.createObjectURL(blob)
  const img = new Image()
  return new Promise<HTMLImageElement>((resolve, reject) => {
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = (e) => {
      URL.revokeObjectURL(url)
      reject(e)
    }
    img.src = url
  })
}

export const loadImage = async (url: string): Promise<HTMLImageElement> => {
  const image = new Image()
  // Canvas must stay un-tainted to be usable as a WebGL texture; logo host
  // needs to send Access-Control-Allow-Origin.
  image.crossOrigin = "anonymous"
  image.src = url
  await image.decode()
  return image
}

const makeRoundedTexture = (
  image: HTMLImageElement,
  radius: number,
  dx: number = 0,
  dy: number = 0
): CanvasTexture => {
  const { naturalWidth: w, naturalHeight: h } = image

  const padLeft = dx > 0 ? Math.round(w * dx) : 0
  const padRight = dx < 0 ? Math.round(w * -dx) : 0
  const padTop = dy < 0 ? Math.round(h * -dy) : 0
  const padBottom = dy > 0 ? Math.round(h * dy) : 0

  const canvas = document.createElement("canvas")
  canvas.width = w + padLeft + padRight
  canvas.height = h + padTop + padBottom

  const ctx = canvas.getContext("2d")

  if (radius > 0) {
    ctx.beginPath()
    ctx.roundRect(padLeft, padTop, w, h, radius)
    ctx.closePath()
    ctx.clip()
  }

  ctx.drawImage(image, padLeft, padTop, w, h)

  const texture = new CanvasTexture(canvas)
  texture.needsUpdate = true

  return texture
}

export const createLogoMaterial = (
  url: string,
  ratio: number = 0.125,
  dx: number = 0,
  dy: number = 0
): SpriteMaterial => {
  const material = new SpriteMaterial({
    color: 0xffffff,
    alphaTest: 0.5,
  })

  loadImage(url)
    .then((image) => {
      const radius = Math.min(image.naturalWidth, image.naturalHeight) * ratio
      material.map = makeRoundedTexture(image, radius, dx, dy)
      material.needsUpdate = true
    })
    .catch((error) => {
      console.warn("Error loading logo image:", url, error)
    })

  return material
}

export const svgToPng = async (svgString: string, scaleFactor: number = 1) => {
  const img = await loadSvgAsImage(svgString)
  const newWidth = img.width * scaleFactor
  const newHeight = img.height * scaleFactor
  const canvas = document.createElement("canvas")
  canvas.width = newWidth
  canvas.height = newHeight
  const ctx = canvas.getContext("2d")!
  ctx.drawImage(img, 0, 0, newWidth, newHeight)
  return canvas.toDataURL("image/png")
}

export const createSVGPathFromMarkerSymbol = (
  style,
  options?: { viewBoxWidth?: number; viewBoxHeight?: number }
) => {
  const {
    markerWidth = 24,
    markerPathWidth,
    markerDx = 0,
    markerDy = 0,
    markerFill,
    markerPath,
    markerCentered = false,
    markerStroke,
    markerStrokeWidth,
    markerFitToBbox = false,
  } = style

  let scale: number
  let dx = markerDx
  let dy = markerDy

  if (markerFitToBbox && markerPath) {
    // Scale so the path's actual bounding box fills markerWidth, then center
    // within the viewBox. Bypasses markerPathWidth and markerCentered.
    const vbW = options?.viewBoxWidth ?? markerWidth
    const vbH = options?.viewBoxHeight ?? vbW
    const fit = getPathTransform(markerPath, vbW, {
      targetSize: markerWidth,
      viewBoxHeight: vbH,
    })
    scale = fit.scale
    dx += fit.dx
    dy += fit.dy
  } else {
    scale = markerWidth / (markerPathWidth ?? 24)
    if (markerCentered && markerPath) {
      // translate applies after scale, so offset must account for scaled bbox
      const vbW = options?.viewBoxWidth ?? markerWidth
      const vbH = options?.viewBoxHeight ?? vbW
      const offset = getPathTransform(markerPath, vbW, {
        scale,
        viewBoxHeight: vbH,
      })
      dx += offset.dx
      dy += offset.dy
    }
  }

  const strokeAttrs = markerStroke
    ? ` stroke="${markerStroke}" stroke-width="${
        markerStrokeWidth ?? 1
      }" stroke-linejoin="round"`
    : ""
  return `<path d="${markerPath}" style="transform:translate(${dx}px, ${dy}px) scale(${scale})" fill="${markerFill}"${strokeAttrs}/>`
}
